/* First-time education: how automatic (maximum) bidding works.
   Shown once per user on active timed lot pages; dismissal persists
   server-side for signed-in users (survives devices), localStorage otherwise. */
import { X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LS_KEY = "aos_proxy_edu_dismissed";

export const ProxyEducationBanner = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const dismissedServer = Boolean(user?.preferences?.proxy_edu_dismissed);
  const dismissedLocal = typeof window !== "undefined" && localStorage.getItem(LS_KEY) === "1";
  const [hidden, setHidden] = useState(false);

  if (hidden || (user ? dismissedServer : dismissedLocal)) return null;

  const dismiss = () => {
    setHidden(true);
    localStorage.setItem(LS_KEY, "1");
    if (user) {
      api.patch("/users/me/preferences", { preferences: { proxy_edu_dismissed: true } })
        .then(() => refreshUser?.())
        .catch(() => {});
    }
  };

  return (
    <div
      data-testid="proxy-education-banner"
      className="rounded-2xl border border-[#1D4D3B]/20 bg-[#1D4D3B]/[0.05] p-4 flex items-start gap-3"
      role="note"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-[#0A0A0A]">{t("bid.eduTitle")}</p>
        <p className="text-xs text-[#3d3d3d] leading-relaxed mt-1">{t("bid.eduBody")}</p>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label={t("bid.eduDismiss")}
        data-testid="proxy-education-dismiss"
        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[#737373] hover:bg-black/5 hover:text-[#0A0A0A] transition-colors duration-200"
      >
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
};
